/**
 * V11.2 — Aperçu PDF rapide d'un livret AITO (impression HTML A4).
 *
 * Approche « le plus rapide » validée par le PO :
 *   1. Récupère la version + snapshot du livret.
 *   2. Rend MemoryBookViewer dans un layout A4 sans chrome (pas de header/footer AITO).
 *   3. Ouvre automatiquement `window.print()` → l'utilisateur choisit
 *      « Enregistrer en PDF » dans le dialog navigateur.
 *
 * Aucune génération serveur (WeasyPrint / ReportLab) : validation visuelle
 * premium en quelques secondes, sans dépendance additionnelle.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Printer } from "lucide-react";
import { MemoryBookViewer } from "@/components/MemoryBookViewer";

export default function AdminMemoryBookPrintPage() {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [version, setVersion] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoPrint, setAutoPrint] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/memory-books/requests/${id}`);
        if (cancelled) return;
        setRequest(data.request);
        setVersion(data.version);
        setSnapshot(data.snapshot);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Impossible de charger le livret.");
        toast.error("Chargement livret impossible");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Auto-print une fois le contenu visible
  useEffect(() => {
    if (!loading && version && autoPrint) {
      const t = setTimeout(() => {
        try { window.print(); } catch { /* noop */ }
      }, 600);
      return () => clearTimeout(t);
    }
  }, [loading, version, autoPrint]);

  return (
    <div className="min-h-screen bg-neutral-100 print:bg-white" data-testid="memorybook-print-page">
      {/* Toolbar (masquée à l'impression) */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to={`/admin/memoires/${id}`}>
              <Button variant="outline" size="sm" data-testid="memorybook-print-back">
                <ArrowLeft className="h-4 w-4 mr-1" /> Retour éditeur
              </Button>
            </Link>
            <div className="text-sm">
              <span className="font-semibold">Aperçu PDF</span>
              {request && (
                <span className="text-muted-foreground ml-2">
                  · {request.entity_name_snapshot || request.entity_type} ·
                  {" "}{request.memory_type}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={autoPrint}
                onChange={(e) => setAutoPrint(e.target.checked)}
                data-testid="memorybook-print-autoprint"
              />
              <span>Ouvrir le dialog d'impression automatiquement</span>
            </label>
            <Button
              size="sm"
              onClick={() => window.print()}
              className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
              data-testid="memorybook-print-btn"
            >
              <Printer className="h-4 w-4 mr-1" /> Imprimer / Enregistrer PDF
            </Button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-2 text-[11px] text-muted-foreground">
          Astuce : dans le dialog d'impression, choisis <em>« Enregistrer en PDF »</em> comme destination
          et <em>« A4 · marges par défaut »</em> pour un rendu premium.
        </div>
      </div>

      {/* Feuille A4 */}
      <div className="max-w-5xl mx-auto py-6 px-2 print:py-0 print:px-0">
        {loading ? (
          <div className="flex items-center justify-center py-24 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Chargement du livret…
          </div>
        ) : error ? (
          <div className="text-center py-24 text-sm text-destructive">{error}</div>
        ) : !version ? (
          <div className="text-center py-24 text-sm text-muted-foreground italic">
            Aucune version générée pour ce livret. Retourne à l'éditeur pour lancer la génération IA.
          </div>
        ) : (
          <div
            className="bg-white shadow-lg print:shadow-none mx-auto p-8 md:p-10 print:p-6"
            style={{
              width: "210mm",
              minHeight: "297mm",
              boxSizing: "border-box",
            }}
            data-testid="memorybook-print-sheet"
          >
            <MemoryBookViewer version={version} snapshot={snapshot} />
          </div>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:p-6 { padding: 12mm !important; }
        }
      `}</style>
    </div>
  );
}
